<script setup>
// Aba Atividades do Painel geral: o que esta atrasado, o que vence hoje e o
// que foi concluido no periodo, no total e por pessoa, e as mais atrasadas.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatNumber, computeTrend } from 'kanban/helper/reportFormatters';
import DashboardAPI from 'vinctoHome/api/dashboard';
import KpiCard from 'vinctoHome/components/KpiCard.vue';

const props = defineProps({
  range: { type: String, default: '7d' },
});

const { t, locale } = useI18n();

const detail = ref({ totals: {}, by_person: [], overdue: [] });
const isLoading = ref(true);
const hasError = ref(false);

const load = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await DashboardAPI.activities({ range: props.range });
    detail.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};
watch(() => props.range, load, { immediate: true });

const kpis = computed(() => [
  {
    key: 'overdue',
    icon: 'i-lucide-alarm-clock-off',
    label: t('VINCTO_HOME.ACTIVITIES.OVERDUE'),
    value: formatNumber(detail.value.totals?.overdue, locale.value),
    accent: !!detail.value.totals?.overdue,
    to: { name: 'crm_calendar' },
  },
  {
    key: 'today',
    icon: 'i-lucide-calendar-clock',
    label: t('VINCTO_HOME.ACTIVITIES.TODAY'),
    value: formatNumber(detail.value.totals?.today, locale.value),
    to: { name: 'crm_calendar' },
  },
  {
    key: 'done',
    icon: 'i-lucide-circle-check',
    label: t('VINCTO_HOME.ACTIVITIES.DONE'),
    value: formatNumber(detail.value.totals?.done, locale.value),
    trend: computeTrend(
      detail.value.totals?.done,
      detail.value.totals?.done_previous
    ),
  },
]);

const formatWhen = value =>
  new Date(value).toLocaleString(String(locale.value).replace('_', '-'), {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <KpiCard
        v-for="card in kpis"
        :key="card.key"
        :label="card.label"
        :value="card.value"
        :icon="card.icon"
        :trend="card.trend"
        :accent="card.accent"
        :to="card.to"
      />
    </div>

    <p v-if="hasError" class="mb-0 text-sm text-n-ruby-11">
      {{ t('VINCTO_HOME.ERROR') }}
    </p>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section
        class="flex flex-col gap-3 p-[18px] border rounded-2xl border-n-weak bg-white dark:bg-n-solid-2"
      >
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('VINCTO_HOME.ACTIVITIES.BY_PERSON') }}
        </h3>
        <p
          v-if="!isLoading && !detail.by_person.length"
          class="mb-0 text-sm text-n-slate-10"
        >
          {{ t('VINCTO_HOME.ACTIVITIES.NOTHING') }}
        </p>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-xs text-left text-n-slate-10">
              <th class="pb-2 font-medium">
                {{ t('VINCTO_HOME.ACTIVITIES.PERSON') }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{ t('VINCTO_HOME.ACTIVITIES.OVERDUE') }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{ t('VINCTO_HOME.ACTIVITIES.TODAY') }}
              </th>
              <th class="pb-2 font-medium text-right">
                {{ t('VINCTO_HOME.ACTIVITIES.DONE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in detail.by_person"
              :key="row.user_id || 'unassigned'"
              class="border-t border-n-weak"
            >
              <td class="py-2 truncate text-n-slate-12">
                {{ row.name || t('VINCTO_HOME.ACTIVITIES.UNASSIGNED') }}
              </td>
              <td
                class="py-2 text-right"
                :class="row.overdue ? 'text-n-amber-11' : 'text-n-slate-12'"
              >
                {{ formatNumber(row.overdue, locale) }}
              </td>
              <td class="py-2 text-right text-n-slate-12">
                {{ formatNumber(row.today, locale) }}
              </td>
              <td class="py-2 text-right text-n-slate-12">
                {{ formatNumber(row.done, locale) }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section
        class="flex flex-col gap-3 p-[18px] border rounded-2xl border-n-weak bg-white dark:bg-n-solid-2"
      >
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('VINCTO_HOME.ACTIVITIES.MOST_OVERDUE') }}
        </h3>
        <p
          v-if="!isLoading && !detail.overdue.length"
          class="mb-0 text-sm text-n-slate-10"
        >
          {{ t('VINCTO_HOME.ACTIVITIES.NONE_OVERDUE') }}
        </p>
        <router-link
          v-for="activity in detail.overdue"
          :key="activity.id"
          :to="{ name: 'crm_calendar' }"
          class="flex items-center justify-between gap-3 px-2 py-2 -mx-2 rounded-lg hover:bg-n-alpha-2"
        >
          <span class="flex flex-col min-w-0">
            <span class="text-sm truncate text-n-slate-12">
              {{ activity.title }}
            </span>
            <span class="text-xs truncate text-n-slate-10">
              {{
                [activity.contact_name, activity.assignee_name]
                  .filter(Boolean)
                  .join(' · ')
              }}
            </span>
          </span>
          <span class="text-xs font-medium shrink-0 text-n-amber-11">
            {{ formatWhen(activity.starts_at) }}
          </span>
        </router-link>
      </section>
    </div>
  </div>
</template>
